<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    variant?: 'info' | 'success' | 'warning' | 'error';
    title?: string;
    content?: string;
    isWide?: boolean;
    canClose?: boolean;
    onClose?: () => void;
  }>(),
  {
    variant: 'info',
    title: '',
    content: '',
    onClose: undefined,
  },
);

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const hidden = ref(false);

function toggleHidden() {
  if (props.onClose !== undefined) {
    emit('close');
  } else {
    hidden.value = !hidden.value;
  }
}
</script>

<template>
  <div
    v-if="!hidden"
    class="ui-alert"
    :data-variant="props.variant"
    :data-wide="props.isWide ? true : null"
  >
    <div class="first-group">
      <UiIcon v-if="props.variant === 'info'" name="Info" class="icon" />
      <UiIcon
        v-if="props.variant === 'success'"
        name="CheckCircle"
        class="icon"
      />
      <UiIcon
        v-if="props.variant === 'warning'"
        name="AlertTriangle"
        class="icon"
      />
      <UiIcon
        v-if="props.variant === 'error'"
        name="AlertCircle"
        class="icon"
      />
      <button
        v-if="canClose && !props.isWide"
        class="close-icon"
        @click="toggleHidden"
      >
        <UiIcon name="XIcon" class="icon" />
      </button>
    </div>

    <div class="central-group">
      <h4 v-if="props.title">{{ props.title }}</h4>
      <p v-if="props.content" class="content">{{ props.content }}</p>
    </div>

    <div>
      <button
        v-if="canClose && props.isWide"
        class="close-icon"
        @click="toggleHidden"
      >
        <UiIcon name="XIcon" class="icon" />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ui-alert {
  display: flex;
  max-width: 24rem;
  flex-direction: column;
  padding: var(--space-16);
  font-size: var(--font-size-14);

  &[data-wide] {
    max-width: none;
    flex-direction: row;
    align-items: center;
  }

  &[data-variant='info'] {
    background: var(--color-bluelight-50);
    color: var(--color-bluelight-800);
  }

  &[data-variant='success'] {
    border: var(--color-success-600) 1px solid;
    background: var(--color-success-25);
    color: var(--color-success-600);
  }

  &[data-variant='warning'] {
    border: var(--color-warning-600) 1px solid;
    background: var(--color-warning-25);
    color: var(--color-warning-600);
    .content {
      color: var(--color-warning-500);
    }
  }

  &[data-variant='error'] {
    border: var(--color-error-600) 1px solid;
    background: var(--color-error-25);
    color: var(--color-error-600);
  }
}

h4 {
  text-transform: uppercase;
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--space-4);
}

.first-group {
  display: flex;
  justify-content: space-between;
}

.icon {
  margin-right: var(--space-10);
  margin-bottom: px(14);
  margin-top: var(--space-12);
}

.close-icon {
  background: transparent;
  cursor: pointer;
  // The icon's margins sit on the button, so the button (and its focus ring)
  // is the size of the icon; the icon stays where it was.
  margin-top: var(--space-12);
  margin-bottom: px(14);
  .icon {
    margin: 0;
  }
}

[data-wide] {
  .central-group {
    margin-top: var(--space-2);
    flex-grow: 1;
  }
}
</style>
